import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CambiarPassResponse,
  CheckRolesModulosResponse,
  Cod_departamento,
  LoginResponse,
  ModuloEnum,
  ShowPerfil,
  PerfilResponse,
  RolEnum,
  RolesModulos,
  actualizarperfil,
  cambiarpass,
  TipoUsuarioEnum,
  PermisoGeneralEnum,
} from '../../shared/interfaces/login/login.interface';
import { AccionPci } from '../../shared/interfaces/permisos-acciones/acciones-pci.interface';

export type DestinoSesion = 'login' | 'dashboard' | 'cambiar-password';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private urlBackend = environment.urlBackend + '/auth';

  private accesos?: RolesModulos;

  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();

  private debeCambiarPasswordSubject = new BehaviorSubject<boolean>(false);
  debeCambiarPassword$ = this.debeCambiarPasswordSubject.asObservable();

  constructor(private http: HttpClient) { }

  // ---------- AUTH ----------

  login(credentials: {
    usuario: string;
    password: string;
  }): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.urlBackend}/login`, credentials, {
        withCredentials: true,
      })
      .pipe(
        tap((resp) => {
          this.isLoggedInSubject.next(true);
          this.setDebeCambiarPassword(resp.usuario.debe_cambiar_password);
        }),
      );
  }

  logout(): Observable<void> {
    return this.http
      .post<void>(`${this.urlBackend}/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => {
          this.isLoggedInSubject.next(false);
          this.accesos = undefined;
          this.debeCambiarPasswordSubject.next(false);
          this.accesosSubject.next(null);
        }),
      );
  }

  getDebeCambiarPassword(): boolean {
    return this.debeCambiarPasswordSubject.value;
  }

  setDebeCambiarPassword(valor: boolean): void {
    this.debeCambiarPasswordSubject.next(valor);
    if (this.accesos) {
      this.accesos = { ...this.accesos, debe_cambiar_password: valor };
      this.accesosSubject.next(this.accesos);
    }
  }

  marcarPasswordActualizada(): void {
    this.setDebeCambiarPassword(false);
  }

  /**
   * Restaura sesión al recargar la app (F5).
   */
  verificarSesion(): Observable<DestinoSesion> {
    return this.checkRolesModulos().pipe(
      map((accesos) =>
        accesos.debe_cambiar_password ? 'cambiar-password' : 'dashboard',
      ),
      catchError(() => {
        this.isLoggedInSubject.next(false);
        return of('login' as DestinoSesion);
      }),
    );
  }

  // ---------- PERFIL ----------

  getPerfil(): Observable<ShowPerfil> {
    return this.http
      .get<PerfilResponse>(`${this.urlBackend}/miperfil`, {
        withCredentials: true,
      })
      .pipe(map((resp) => resp.perfil[0]));
  }

  cambiarImagen(file: File) {
    const formData = new FormData();
    formData.append('imagen', file);

    return this.http.put(`${this.urlBackend}/cambiar-imagen`, formData, {
      withCredentials: true,
    });
  }

  // ---------- ROLES & MÓDULOS ----------

  private accesosSubject = new BehaviorSubject<RolesModulos | null>(null);
  accesos$ = this.accesosSubject.asObservable();

  checkRolesModulos(): Observable<RolesModulos> {
    return this.http
      .get<CheckRolesModulosResponse>(`${this.urlBackend}/checkrolesmodulos`, {
        withCredentials: true,
      })
      .pipe(
        map((resp) => resp.CheckRolesModulos[0]),
        tap((accesos) => {
          this.accesos = accesos;
          this.accesosSubject.next(accesos);
          this.isLoggedInSubject.next(true);
          this.setDebeCambiarPassword(accesos.debe_cambiar_password === true);
        }),
      );
  }

  hasPermisoGeneral(permiso: PermisoGeneralEnum): boolean {
    return this.accesos?.permisos_generales?.includes(permiso) ?? false;
  }

  getAccesos(): RolesModulos | undefined {
    return this.accesos;
  }

  ActualizarPerfil(Datos: actualizarperfil) {
    return this.http.put(`${this.urlBackend}/actualizar-perfil`, Datos, {
      withCredentials: true,
    });
  }

  Cambiarcontrasena(Pass: cambiarpass): Observable<CambiarPassResponse> {
    return this.http
      .put<CambiarPassResponse>(`${this.urlBackend}/cambiar-contrasena`, Pass, {
        withCredentials: true,
      })
      .pipe(tap(() => this.marcarPasswordActualizada()));
  }

  hasRole(role: RolEnum): boolean {
    const roles = this.accesos?.roles ?? [];

    if (roles?.includes(RolEnum.SUPERADMINISTRADOR)) {
      return true;
    }

    return roles.includes(role);
  }

  hasRoleItem(role: RolEnum): Observable<boolean> {
    return this.accesos$.pipe(
      map((accesos) => accesos?.roles.includes(role) ?? false),
    );
  }

  hasTiposAsistentesItem(tipo: TipoUsuarioEnum): Observable<boolean> {
    return this.accesos$.pipe(
      map((accesos) => accesos?.tipos_asistentes.includes(tipo) ?? false),
    );
  }

  hasCodUnidad(
    unidad: Cod_departamento,
  ): Observable<boolean> {
    return this.accesos$.pipe(
      map(
        (accesos) =>
          accesos?.cod_unidad === unidad,
      ),
    );
  }

  hasModulo(modulo: ModuloEnum): Observable<boolean> {
    return this.accesos$.pipe(
      map((accesos) => accesos?.modulos.includes(modulo) ?? false),
    );
  }

  hasAccionGlobal(
    accion: string,
  ): boolean {
    if (
      this.accesos?.roles.includes(
        RolEnum.SUPERADMINISTRADOR,
      )
    ) {
      return true;
    }

    return (
      this.accesos
        ?.acciones_globales
        ?.includes(accion) ?? false
    );
  }

  hasAccionGlobalItem(
    accion: string,
  ): Observable<boolean> {
    return this.accesos$.pipe(
      map((accesos) => {
        if (!accesos) {
          return false;
        }

        if (
          accesos.roles.includes(
            RolEnum.SUPERADMINISTRADOR,
          )
        ) {
          return true;
        }

        return (
          accesos.acciones_globales
            ?.includes(accion) ?? false
        );
      }),
    );
  }
}
